<script setup lang="ts">
import { computed, ref } from 'vue'
import { LogOut, MessageSquare, MessageSquarePlus, X } from 'lucide-vue-next'
import { Button } from '@/lib/ui/button'
import { Avatar, AvatarFallback } from '@/lib/ui/avatar'
import { Separator } from '@/lib/ui/separator'
import { ConfirmationDialog } from '@/components/ConfirmationDialog'
import { FeedbackDialog } from '@/components/FeedbackDialog'

interface IWebDirectSidebarProps {
  readonly logoUrl?: string
  readonly fallbackLogoUrl?: string
  readonly name?: string
  readonly isAuthenticated?: boolean
  readonly userName?: string | null
  readonly userDisplayName?: string | null
  readonly appVersion?: string
  readonly closeable?: boolean
}

interface IWebDirectSidebarEmits {
  logout: []
  close: []
}

const props = defineProps<IWebDirectSidebarProps>()
const emit = defineEmits<IWebDirectSidebarEmits>()

const isLogoutConfirmOpen = ref(false)
const isFeedbackOpen = ref(false)

const userInitials = computed(() => {
  const name = props.userName
  if (!name) return '?'
  return String(name[0]).toUpperCase()
})
</script>

<template>
  <aside
    class="w-64 h-full shrink-0 flex flex-col pt-5 px-4 pb-4 bg-sidebar-background border-r border-white/5 overflow-hidden"
    aria-label="Chatbot sidebar"
  >
    <div class="flex items-center gap-3 pb-1">
      <div
        class="size-11 shrink-0 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden"
      >
        <img
          v-if="logoUrl"
          :src="logoUrl"
          :alt="name"
          class="w-full h-full object-cover"
        />
        <div
          v-else-if="fallbackLogoUrl"
          class="flex items-center justify-center w-full h-full"
        >
          <img
            :src="fallbackLogoUrl"
            alt="Sidd"
            class="size-7 object-contain"
          />
        </div>
        <MessageSquare v-else class="size-5 text-white/60" />
      </div>

      <div class="flex flex-col min-w-0 flex-1">
        <span
          class="text-sm font-semibold text-sidebar-foreground truncate leading-snug"
        >
          {{ name || 'Ask Sidd' }}
        </span>
        <span class="text-xs text-white/40 uppercase tracking-wider mt-0.5">
          Powered by Sidd
        </span>
      </div>

      <Button
        v-if="closeable"
        variant="ghost"
        size="icon"
        class="size-8 shrink-0 text-white/50 hover:bg-white/10 hover:text-white"
        aria-label="Close sidebar"
        @click="emit('close')"
      >
        <X class="size-4" />
      </Button>
    </div>

    <Separator class="my-4 bg-white/10" />

    <!-- TODO: re-enable when multi-session support is added -->
    <!-- <div class="flex items-center gap-2 text-xs text-white/30 px-0.5">
      <MessageSquare class="size-3.5 shrink-0" />
      <span>New conversation each session</span>
    </div> -->

    <div class="flex-1" />

    <!-- Action menu -->
    <div class="flex flex-col gap-2">
      <Button
        variant="ghost"
        class="w-full h-auto flex items-center gap-3 px-2.5 py-2 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 hover:text-white justify-start"
        aria-label="Share feedback"
        @click="isFeedbackOpen = true"
      >
        <MessageSquarePlus class="size-4 shrink-0" />
        <span class="text-sm font-medium">Feedback</span>
      </Button>

      <Button
        v-if="isAuthenticated"
        variant="ghost"
        class="w-full h-auto flex items-center gap-3 px-2.5 py-2 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 hover:text-red-400 justify-start"
        aria-label="Logout"
        @click="isLogoutConfirmOpen = true"
      >
        <LogOut class="size-4 shrink-0" />
        <span class="text-sm font-medium">Logout</span>
      </Button>
    </div>

    <Separator class="my-3 bg-white/10" />

    <!-- User card -->
    <div
      v-if="isAuthenticated"
      class="wd-sidebar-user flex items-center gap-2.5 p-2.5 rounded-xl bg-white/5 border border-white/10"
    >
      <Avatar class="size-8 shrink-0">
        <AvatarFallback class="bg-transparent text-white text-xs font-bold">
          {{ userInitials }}
        </AvatarFallback>
      </Avatar>

      <div class="flex flex-col min-w-0 flex-1">
        <span
          class="text-sm font-medium text-sidebar-foreground truncate leading-snug"
        >
          {{ userName || 'User' }}
        </span>
        <span class="text-xs text-white/40 mt-px truncate">
          {{ userDisplayName || userName || 'User' }}
        </span>
      </div>
    </div>

    <span
      v-if="appVersion"
      class="text-xs text-white/20 pt-3 text-right w-full"
    >
      v{{ appVersion }}
    </span>

    <ConfirmationDialog
      v-model:open="isLogoutConfirmOpen"
      title="Logout"
      description="Are you sure you want to log out?"
      confirm-label="Logout"
      cancel-label="Cancel"
      :destructive="true"
      @confirm="emit('logout')"
    />

    <FeedbackDialog
      v-model:open="isFeedbackOpen"
      :app-version="appVersion"
      :user-name="userName"
    />
  </aside>
</template>

<style scoped>
.wd-sidebar-user :deep([data-slot='avatar']) {
  background-color: hsl(var(--primary));
}
</style>
